import React, { useEffect, useRef } from 'react';
import {
    Pagination,
    List,
    DatagridConfigurable,
    TextField,
    DateField,
    BooleanField,
    ReferenceField,
    EditButton,
    sanitizeListRestProps,
    TopToolbar,
    ShowButton,
    CreateButton,
    useListContext} from 'react-admin';
import OrderListShow from './OrderListShow';
import OrderListFilterForm from './OrderListFilterForm';
import OrderCloneButton from './OrderCloneButton';
import { Box, Chip } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import InboxIcon from '@mui/icons-material/Inbox';
import { orderListDataGridSx } from '../styles/dataGridStyles';

const CheckBoxIconBlack = () => (
    <CheckBoxIcon style={{ color: 'black' }} />
);

const CheckBoxOutlineBlankIconBlack = () => (
    <CheckBoxOutlineBlankIcon style={{ color: 'black' }} />
);

const CheckBoxIconRed = () => (
    <CheckBoxIcon style={{ color: 'red' }} />
);

const CheckBoxOutlineBlankIconRed = () => (
    <CheckBoxOutlineBlankIcon style={{ color: 'red' }} />
);

const CheckBoxIconBlue = () => (
    <CheckBoxIcon style={{ color: 'blue' }} />
);

const CheckBoxOutlineBlankIconBlue = () => (
    <CheckBoxOutlineBlankIcon style={{ color: 'blue' }} />
);

const OrderPagination = props => <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />;

const OrderListActions = (props) => {
  const {
    exporter,
    filters,
    maxResults,
    setFilters,
    ...rest
  } = props;
  return (
    <TopToolbar {...sanitizeListRestProps(rest)}>
      <Box display="flex" justifyContent="flex-end" width="100%">
        <OrderListFilterForm />
      </Box>
      <Box display="flex" justifyContent="flex-end" width="100%">
        <OrderPagination />
      </Box>
    </TopToolbar>
  );
};

const QuickFilters = () => {
    const { filterValues, setFilters, expanded, onToggleItem } = useListContext();
    const hasClearedExpandedRef = useRef(false);

    // Clear expanded rows when they exist and we haven't cleared them yet
    useEffect(() => {
        if (!hasClearedExpandedRef.current && expanded && expanded.length > 0 && onToggleItem) {
            // Collapse all expanded rows
            [...expanded].forEach(id => {
                onToggleItem(id);
            });
            hasClearedExpandedRef.current = true;
        }

        // Reset flag on unmount so it works on next navigation
        return () => {
            hasClearedExpandedRef.current = false;
        };
    }, [expanded, onToggleItem]);

    const handleFilterClick = (newFilters) => {
        setFilters(newFilters, null);
    };

    const isActiveFilter = (filterKey, filterValue) => {
        if (typeof filterValue === 'object') {
            return Object.keys(filterValue).every(key => filterValues[key] === filterValue[key]);
        }
        return filterValues[filterKey] === filterValue;
    };

    const clearFilters = () => {
        setFilters({}, null);
    };

    return (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Box sx={{ fontWeight: 'bold', mr: 1 }}>Quick Filters:</Box>

            {/* Status Filters */}
            <Chip
                label="Pending"
                icon={<CheckBoxIcon />}
                onClick={() => handleFilterClick({ Pending: true })}
                color={isActiveFilter('Pending', true) ? 'primary' : 'default'}
                variant={isActiveFilter('Pending', true) ? 'filled' : 'outlined'}
            />
            <Chip
                label="Not Sent"
                icon={<CheckBoxOutlineBlankIcon />}
                onClick={() => handleFilterClick({ Not_Sent: true })}
                color={isActiveFilter('Not_Sent', true) ? 'primary' : 'default'}
                variant={isActiveFilter('Not_Sent', true) ? 'filled' : 'outlined'}
            />

            {/* Clear Filters */}
            {Object.keys(filterValues).length > 0 && (
                <Chip
                    label="Clear All"
                    onClick={clearFilters}
                    color="default"
                    variant="outlined"
                    sx={{ ml: 1 }}
                />
            )}
        </Box>
    );
};

const OrderEmpty = () => (
    <Box textAlign="center" m={1}>
        <InboxIcon sx={{ fontSize: 100, color: 'text.disabled' }} />
        <Box mt={2} mb={1} sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            No orders yet
        </Box>
        <Box mb={2} sx={{ color: 'text.secondary' }}>
            Create your first order to get started
        </Box>
        <CreateButton />
    </Box>
);

const OrderList = props => {
    return (
        <List
            sort={{ field: 'Order_Num', order: 'DESC' }}
            perPage={100}
            pagination={<OrderPagination />}
            bulkActionButtons={false}
            actions={<OrderListActions sx={{ paddingTop: '0px', display: 'block' }}/>}
            empty={<OrderEmpty />}
            {...props}
        >
            <QuickFilters />
            <DatagridConfigurable
                rowClick="expand"
                expand={<OrderListShow />}
                sx={orderListDataGridSx}
            >
                <TextField label="Order Number" source="Order_Num" />
                <TextField label="Type" source="Order_Type" sortable={false} />
                <ReferenceField label="Customer" source="CustomerID" reference="customers" sortable={false}>
                    <TextField source="Customer" />
                </ReferenceField>
                <ReferenceField label="Trucking Company" source="CarrierID" reference="truckingcompanies" sortable={false}>
                    <TextField source="Trucking_Company" />
                </ReferenceField>
                <DateField label="Delivery Date" source="Delivery_Date" sortable={false} />
                <BooleanField label="Pending" source="Pending" TrueIcon={CheckBoxIconBlack} FalseIcon={CheckBoxOutlineBlankIconBlack} />
                <BooleanField label="PUN" source="Emailed_PickUp_Notice" TrueIcon={CheckBoxIconBlue} FalseIcon={CheckBoxOutlineBlankIconBlue} />
                <BooleanField label="TM" source="Emailed_Trucking_Manifest" TrueIcon={CheckBoxIconRed} FalseIcon={CheckBoxOutlineBlankIconRed} />
                <EditButton />
                <ShowButton />
                <OrderCloneButton record={props.record} />
            </DatagridConfigurable>
        </List>
    );
}

export default OrderList;
